import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Button,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import httpRequest from "../utils/httpRequest";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import html2canvas from "html2canvas";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import CreateInvoiceDialog from "./CreateInvoiceDialog";

const InvoiceTable = ({
  invoices,
  setPage,
  setPageSize,
  pageSize,
  page,
  fetchInvoices,
}) => {
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [duplicateTargetInvoice, setDuplicateTargetInvoice] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
  const [loadingWhatsappIds, setLoadingWhatsappIds] = useState([]);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleApproveOrReject = async (invoice_number, action) => {
    try {
      await httpRequest("invoice/approveOrRejectTempInvoice", "POST", {
        invoice_number,
        action,
      });
      setSnackbarMessage(`Invoice ${action}d successfully!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchInvoices(page, pageSize);
    } catch (error) {
      console.error(`Error on ${action} invoice:`, error);
      setSnackbarMessage(`Error trying to ${action} invoice.`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setPage(1);
  };

  const viewPdf = async (invoice_number) => {
    try {
      setLoadingInvoiceId(invoice_number);
      const invoiceHtml = await httpRequest(
        "invoice/generateHtmlForInvoice",
        "GET",
        {},
        {
          invoice_number: invoice_number,
        }
      );

      if (!invoiceHtml) {
        console.error("Failed to fetch invoice HTML");
        setLoadingInvoiceId(null);
        return null;
      }

      const tempElement = document.createElement("div");
      tempElement.innerHTML = invoiceHtml;
      tempElement.style.position = "fixed";
      tempElement.style.left = "-9999px";
      document.body.appendChild(tempElement);

      await Promise.all(
        Array.from(tempElement.querySelectorAll("img")).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          });
        })
      );

      const canvas = await html2canvas(tempElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginX = 5;
      const marginY = 5;
      const imgWidth = pageWidth - marginX * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let position = 0;
      let pageHeightPx = (pageHeight * canvas.width) / pageWidth;

      while (position < canvas.height) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(pageHeightPx, canvas.height - position);

        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0, position,
          canvas.width, pageCanvas.height,
          0, 0,
          canvas.width, pageCanvas.height
        );

        const pageData = pageCanvas.toDataURL("image/png");
        if (position > 0) pdf.addPage();
        pdf.addImage(pageData, "PNG", marginX, marginY, imgWidth, (pageCanvas.height * imgWidth) / canvas.width);

        position += pageHeightPx;
      }


      const now = new Date();
      const dateTimeString = now
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "_")
        .slice(0, -5);
      const fileName = `${invoice_number}_${dateTimeString}.pdf`;

      const blob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);

      document.body.removeChild(tempElement);

      setLoadingInvoiceId(null);
      return blobUrl;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setLoadingInvoiceId(null);
      return null;
    }
  };

  const handleOpen = async (invoice) => {
    const pdfUrl = await viewPdf(invoice.invoice_number);
    if (pdfUrl) {
      setSelectedPdfUrl(pdfUrl);
      setSelectedInvoice(invoice);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPdfUrl(null);
    setSelectedInvoice(null);
  };

  const handleEditInvoice = async (invoice) => {
    try {
      const invoiceData = await httpRequest(`invoice/getInvoiceById/${invoice.id}`, "GET");
      setEditInvoice(invoiceData);
      setEditDialogOpen(true);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      setSnackbarMessage("Error fetching invoice data");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditInvoice(null);
  };

  const handleSendWhatsapp = async (invoice) => {
    try {
      setLoadingWhatsappIds((prev) => [...prev, invoice.invoice_number]);
      console.log('Starting WhatsApp send for invoice:', invoice.invoice_number);
      
      try {
        const whatsappStatus = await httpRequest('whatsapp/status', 'GET');
        console.log('WhatsApp status:', whatsappStatus);
        if (!whatsappStatus.isReady) {
          throw new Error("WhatsApp is not connected. Please scan the QR code in the backend terminal.");
        }
      } catch (statusError) {
        console.error('WhatsApp status check failed:', statusError);
        throw new Error("Unable to check WhatsApp connection. Please ensure the backend is running and WhatsApp is connected.");
      }
      
      const invoiceHtml = await httpRequest(
        "invoice/generateHtmlForInvoice",
        "GET",
        {},
        {
          invoice_number: invoice.invoice_number,
        }
      );

      if (!invoiceHtml) {
        throw new Error("Failed to fetch invoice HTML");
      }

      console.log('Invoice HTML generated successfully');

      const tempElement = document.createElement("div");
      tempElement.innerHTML = invoiceHtml;
      tempElement.style.position = "fixed";
      tempElement.style.left = "-9999px";
      document.body.appendChild(tempElement);

      await Promise.all(
        Array.from(tempElement.querySelectorAll("img")).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          });
        })
      );

      const canvas = await html2canvas(tempElement, { 
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.5);

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginX = 5;
      const marginY = 5;
      const imgWidth = pageWidth - marginX * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let position = 0;
      let pageHeightPx = (pageHeight * canvas.width) / pageWidth;

      while (position < canvas.height) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(pageHeightPx, canvas.height - position);

        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0, position,
          canvas.width, pageCanvas.height,
          0, 0,
          canvas.width, pageCanvas.height
        );

        const pageData = pageCanvas.toDataURL("image/jpeg", 0.5);
        if (position > 0) pdf.addPage();
        pdf.addImage(pageData, "JPEG", marginX, marginY, imgWidth, (pageCanvas.height * imgWidth) / canvas.width);

        position += pageHeightPx;
      }

      document.body.removeChild(tempElement);

      const pdfBlob = pdf.output("blob");
      console.log('PDF generated, size:', pdfBlob.size, 'bytes');
      
      const customerMobile = invoice.customer_mobile || '';
      console.log('Customer mobile:', customerMobile);
      
      if (!customerMobile || customerMobile === 'NA' || customerMobile.trim() === '') {
        throw new Error("Customer mobile number not found or invalid. Please update the customer's mobile number first.");
      }

      const formData = new FormData();
      formData.append('pdfFile', pdfBlob, `${invoice.invoice_number}.pdf`);
      formData.append('recipient', customerMobile);
      formData.append('fileName', `${invoice.invoice_number}.pdf`);
      formData.append('message', `${invoice.invoice_firm_name} has sent you invoice ${invoice.invoice_number} dated ${new Date(invoice.invoice_date).toLocaleDateString()}`);
      formData.append('invoiceId', invoice.id);

      console.log('Sending to WhatsApp endpoint...');
      
      const response = await httpRequest('whatsapp/send', 'POST', formData, null, 'json', true);
      
      console.log('WhatsApp response:', response);

      setSnackbarMessage("Invoice sent via WhatsApp successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      setTimeout(() => {
        fetchInvoices(page, pageSize);
      }, 1000);
    } catch (error) {
      console.error("Error sending WhatsApp:", error);
      console.error("Error details:", error.response?.data);
      setSnackbarMessage(error.response?.data?.error || error.message || "Failed to send invoice via WhatsApp");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingWhatsappIds((prev) => prev.filter((id) => id !== invoice.invoice_number));
    }
  };

const handleInvoiceUpdated = () => {
        setSnackbarMessage("Invoice updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEditDialogOpen(false);
        setEditInvoice(null);
        fetchInvoices();
      };

  const isValidMobileNumber = (mobile) => {
    if (!mobile || mobile === 'NA' || mobile.trim() === '') return false;
    const cleanMobile = mobile.replace(/\D/g, '');
    return cleanMobile.length >= 10;
  };

      return (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="invoice table">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Invoice Date</TableCell>
                  <TableCell>Invoice Generated by Firm</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Customer Location</TableCell>
                  <TableCell>GSTIN</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.data.length ? (
                  invoices.data.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      sx={invoice.is_temp ? { backgroundColor: "#fff3cd" } : {}}
                    >
                      <TableCell>{invoice.invoice_number}</TableCell>

                      <TableCell>
                        {new Date(invoice.invoice_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{invoice.invoice_firm_name}</TableCell>
                      <TableCell>{invoice.grand_total}</TableCell>
                      <TableCell>
                        {invoice.firm_name} ({invoice.account_number})
                      </TableCell>
                      <TableCell>{invoice.customer_location}</TableCell>
                      <TableCell>{invoice.gst_number}</TableCell>
                      <TableCell>
                        {invoice.is_cn ? "Credit Note" : invoice.type === "gst" ? "GST" : "Kachcha"}
                      </TableCell>

                      <TableCell sx={{ display: "flex" }}>
                        <Tooltip title="View PDF">
                          <button
                            onClick={() => handleOpen(invoice)}
                            style={{
                              cursor: "pointer",
                              background: "none",
                              border: "none",
                              color: "#555",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              transition: "transform 0.1s ease, opacity 0.2s ease",
                            }}
                            onMouseDown={(e) =>
                              (e.currentTarget.style.transform = "scale(0.85)")
                            }
                            onMouseUp={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                          >
                            {loadingInvoiceId === invoice.invoice_number ? (
                              <CircularProgress size={20} />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </button>
                        </Tooltip>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {invoice.is_temp ? (
                            <>
                              <Tooltip title="Edit Invoice">
                                <EditIcon
                                  fontSize="small"
                                  color="primary"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    handleEditInvoice(invoice);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Approve Invoice">
                                <CheckIcon
                                  fontSize="small"
                                  color="success"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (window.confirm("Are you sure you want to approve this invoice?")) {
                                      handleApproveOrReject(invoice.invoice_number, "approve");
                                    }
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Reject Invoice">
                                <CloseIcon
                                  fontSize="small"
                                  color="error"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (window.confirm("Are you sure you want to reject this invoice?")) {
                                      handleApproveOrReject(invoice.invoice_number, "reject");
                                    }
                                  }}
                                />
                              </Tooltip>
                            </>
                          ) : null}
                          <Tooltip title="Duplicate Invoice">
                            <ContentCopyIcon
                              fontSize="small"
                              color="primary"
                              style={{ cursor: "pointer", marginRight: 8 }}
                              onClick={() => {
                                setDuplicateTargetInvoice(invoice);
                                setConfirmDuplicateOpen(true);
                              }}
                            />
                          </Tooltip>
                          {!invoice.is_temp && (
                            <Tooltip 
                              title={
                                !isValidMobileNumber(invoice.customer_mobile) 
                                  ? "Customer mobile number is missing or invalid" 
                                  : "Send via WhatsApp"
                              }
                            >
                              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {loadingWhatsappIds.includes(invoice.invoice_number) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <>
                                    <WhatsAppIcon
                                      fontSize="small"
                                      color={isValidMobileNumber(invoice.customer_mobile) ? "success" : "disabled"}
                                      style={{ 
                                        cursor: isValidMobileNumber(invoice.customer_mobile) ? "pointer" : "not-allowed",
                                        opacity: isValidMobileNumber(invoice.customer_mobile) ? 1 : 0.5
                                      }}
                                      onClick={() => {
                                        if (isValidMobileNumber(invoice.customer_mobile)) {
                                          handleSendWhatsapp(invoice);
                                        }
                                      }}
                                    />
                                    {invoice.whatsapp_notification_count > 0 && (
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          color: invoice.whatsapp_sent_count > 0 ? "#2e7d32" : "#d84315",
                                          fontWeight: "bold",
                                          marginTop: "2px",
                                          lineHeight: "1",
                                          backgroundColor: invoice.whatsapp_sent_count > 0 ? "#e8f5e8" : "#ffebee",
                                          padding: "1px 4px",
                                          borderRadius: "4px",
                                          border: `1px solid ${invoice.whatsapp_sent_count > 0 ? "#4caf50" : "#ff5722"}`,
                                        }}
                                        title={`${invoice.whatsapp_sent_count || 0} sent / ${invoice.whatsapp_notification_count} total WhatsApp notifications`}
                                      >
                                        {invoice.whatsapp_notification_count}
                                      </span>
                                    )}
                                  </>
                                )}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9}>No invoices found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
          >
            <FormControl size="small">
              <InputLabel id="page-size-label">Rows</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                onChange={handlePageSizeChange}
                label="Rows"
              >
                {[5, 10, 20, 50].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Pagination
              count={invoices.pagination?.totalPages || 1}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
          <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
              }}
            >
              <Typography variant="h6">
                Invoice Preview - {selectedInvoice?.invoice_number}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href={selectedPdfUrl}
                download={`${selectedInvoice?.invoice_number}_${new Date()
                  .toISOString()
                  .replace(/[:.]/g, "-")
                  .replace("T", "_")
                  .slice(0, -5)}.pdf`}
                sx={{ ml: 2, whiteSpace: "nowrap" }}
                startIcon={<DownloadIcon />}
              >
                Download PDF
              </Button>
            </DialogTitle>
            <DialogContent dividers>
              {selectedPdfUrl && (
                <>
                  <Box mb={2} textAlign="right"></Box>
                  <iframe
                    src={selectedPdfUrl}
                    title="PDF Preview"
                    width="100%"
                    height="600px"
                    style={{ border: "none" }}
                  />
                </>
              )}
            </DialogContent>
          </Dialog>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          {editInvoice && (
            <CreateInvoiceDialog
              open={editDialogOpen}
              onClose={handleCloseEditDialog}
              onCloseAlert={handleCloseEditDialog}
              onInvoiceCreated={handleInvoiceUpdated}
              type={editInvoice.type}
              editMode={true}
              invoiceData={editInvoice}
            />
          )}
          <Dialog
            open={confirmDuplicateOpen}
            onClose={() => setConfirmDuplicateOpen(false)}
          >
            <DialogTitle>Duplicate Invoice</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to duplicate invoice{' '}
                <b>{duplicateTargetInvoice?.invoice_number}</b>?
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => setConfirmDuplicateOpen(false)} color="inherit">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    try {
                      await httpRequest(`invoice/duplicate/${duplicateTargetInvoice.id}`, "POST");
                      setSnackbarMessage("Invoice copied successfully!");
                      setSnackbarSeverity("success");
                      setSnackbarOpen(true);
                      fetchInvoices();
                    } catch (error) {
                      setSnackbarMessage("Failed to copy invoice");
                      setSnackbarSeverity("error");
                      setSnackbarOpen(true);
                    } finally {
                      setConfirmDuplicateOpen(false);
                      setDuplicateTargetInvoice(null);
                    }
                  }}
                >
                  Duplicate
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      );
    };

    export default InvoiceTable;
